import { instantFor } from '../capture/dates';
import { renderText } from './meetingReport';

// Approving a meeting write-up, through the ordinary routes, in an order
// that leaves nothing orphaned: the new contacts and colleagues first, so
// the entry can point at them; then the Meeting activity (the report as its
// description, the person's own words as its raw capture); then the next
// steps as action items; then everyone who attended, linked to the entry. A
// person the server refuses never blocks the entry. A refused entry holds
// the rest back and is reported. Every write carries a client id derived
// from `base`, so running this again after a failure replays safely.
//
// `api` is injected so the orchestration can be tested without the network.

const message = (err, fallback) => (err && err.response && err.response.data && err.response.data.error) || (err && err.message) || fallback;
const on = (list) => (list || []).filter((p) => p.include !== false);

export async function applyMeetingReport(proposal, { api, user, base, onProgress = () => {} }) {
  const report = proposal.payload;
  const key = (suffix) => `${base}-${suffix}`.slice(0, 64);
  const created = { contacts: [], internal_people: [], tasks: [] };
  const errors = [];
  const attendees = [];

  onProgress('people');
  for (const person of on(report.customer_attendees)) {
    if (!person.is_new) { attendees.push({ contact_id: person.contact_id }); continue; }
    try {
      const res = await api.createContact({ name: person.name, job_title: person.title || undefined, email: person.email || undefined, account_id: report.account_id, client_id: key(person.key) });
      created.contacts.push(res.data);
      attendees.push({ contact_id: res.data.id });
    } catch (err) {
      errors.push({ step: 'contact', key: person.key, name: person.name, message: message(err, 'Could not add this person') });
    }
  }
  for (const person of on(report.internal_attendees)) {
    if (person.user_id) { attendees.push({ user_id: person.user_id }); continue; }
    if (person.internal_person_id) { attendees.push({ internal_person_id: person.internal_person_id }); continue; }
    try {
      const res = await api.createInternalPerson({ name: person.name, title: person.title || undefined });
      created.internal_people.push(res.data);
      attendees.push({ internal_person_id: res.data.id });
    } catch (err) {
      errors.push({ step: 'internal', key: person.key, name: person.name, message: message(err, 'Could not add this colleague') });
    }
  }

  onProgress('activity');
  const firstContact = attendees.find((a) => a.contact_id);
  let activity;
  try {
    const res = await api.createActivity({
      type: 'Meeting',
      description: renderText(report),
      activity_date: instantFor(report.meeting_date),
      account_id: report.account_id,
      contact_id: firstContact ? firstContact.contact_id : null,
      opportunity_id: report.opportunity_id || null,
      raw_capture: proposal.raw || null,
      source: 'web',
      ai_model: proposal.ai_model || null,
      prompt_version: proposal.prompt_version || null,
      client_id: key('act'),
    });
    activity = res.data;
  } catch (err) {
    errors.push({ step: 'activity', message: message(err, 'Could not save the meeting') });
    return { activity: null, created, errors };
  }

  onProgress('tasks');
  for (const step of (report.next_steps || []).filter((n) => n.as_task !== false)) {
    try {
      const res = await api.createTask({
        title: step.title,
        due_date: step.due_date ? `${step.due_date}T09:00` : null,
        account_id: report.account_id,
        contact_id: null,
        opportunity_id: report.opportunity_id || null,
        assigned_to: step.owner_user_id || (user ? user.id : undefined),
        client_id: key(step.key),
      });
      created.tasks.push(res.data);
    } catch (err) {
      errors.push({ step: 'task', key: step.key, title: step.title, message: message(err, 'Could not add this next step') });
    }
  }

  onProgress('attendees');
  if (attendees.length) {
    try { await api.addActivityAttendees(activity.id, attendees); }
    catch (err) { errors.push({ step: 'attendees', message: message(err, 'Could not link the attendees') }); }
  }

  return { activity, created, errors };
}
